/**
 * The buildings standing on a plot, drawn as buildings.
 *
 * A placed structure used to be a coloured hexagon in the surface shader and
 * nothing else: from the walkaround's own eye height a refinery and an
 * extractor were two flat tints. This draws each one as a compound of
 * low-poly parts built from primitives in code - halls, hoppers, columns,
 * cryo spheres, regolith-bermed vaults, radiator wings, battery rows, masts -
 * with the footprint the shader already paints kept as the pad under them.
 *
 * Every part is its own instance, so it stands on the ground under ITSELF
 * rather than on the compound's centre: a 150 m compound on a buildable slope
 * at the default 2.5x relief spans metres of height, and one base height would
 * float half of it. Parts carry a sintered foundation that runs well below
 * their base for the same reason. One `InstancedMesh` per part type and level
 * of detail, shared by every building that uses the part, so the draw count
 * follows the part types on screen and not the buildings.
 *
 * Lit by the plot's own sun uniforms by reference, the way `rockField.ts`'s
 * scatter is, and shaded procedurally in plot-local metres: no UVs and no
 * texture, because a primitive's UVs are useless and the ground under it is
 * already triplanar. The terrain's own shadow reaches a building through one
 * CPU march per building off the drawn heights, the cascades through
 * `PLOT_CSM_GLSL` when the plot has them.
 *
 * Levels of detail are per building, off its range from the eye: NEAR draws
 * every part at full segment count and casts; MID draws only the major parts
 * at a low segment count and does not cast; FAR draws nothing but the pad and
 * the job beacon, which keeps a minimum size in pixels so an active building
 * still reads from across the plot.
 */
import * as THREE from 'three';

import type { StructureMode } from './structureStatus';
import { HAUL_CAP, haulPose, type HaulPoint, type HaulRoute } from './structureHauls.ts';
import { devFlag, urlFlag, TEST_SWITCHES } from '../urlFlags.ts';

/**
 * `?nohaulredraw=1`: each hauler drawn once where it first stood and never
 * moved again - `structure-visuals-check --inject no-redraw`, the control for
 * a bar that has to read the drawn matrices and not the clock.
 */
const haulsFrozen = devFlag(() => TEST_SWITCHES && urlFlag('nohaulredraw'));

/**
 * `?oldsunguard=1`: repaint on any seen change in the sun direction, the
 * exact-equality guard this file threw out - `structure-visuals-check
 * --inject old-guard`, the control for the repaint-rate reading.
 */
const oldSunGuard = devFlag(() => TEST_SWITCHES && urlFlag('oldsunguard'));

/** One structure standing on the plot, as `DtmPlot.setStructures` receives it. */
export interface StructureEntry {
  x: number;
  z: number;
  halfM: number;
  classId?: number;
  kind?: string;
  cell?: string;
  buildingId?: string;
}

/** What the crafting state says, pushed per structure key (`cell:kind`). */
export interface StructureLook {
  mode: StructureMode;
  /** Linear RGB of the class's status light. */
  accent: readonly [number, number, number];
}

export interface StructureMeshesOpts {
  /** The plot's uniform bag: sun, sky, bounce, eye, clock and, when present, the cascades. */
  readonly uniforms: Record<string, THREE.IUniform>;
  /** The drawn ground at plot-local xz, exaggerated metres (`DtmPlot.meshHeightAt`). */
  heightAt(x: number, z: number): number;
  /** `PLOT_CSM_GLSL`, or null on a plot built without cascades. */
  readonly csmGlsl: string | null;
}

export type StructureTier = 'near' | 'mid' | 'far';

export interface StructureMeshStats {
  buildings: number;
  tiers: Record<StructureTier, number>;
  /** Part instances drawn, across every mesh. */
  instances: number;
  /** Meshes with anything to draw: the draw calls this adds before shadows. */
  meshes: number;
  triangles: number;
  beacons: number;
  plumes: number;
  /** Vents breathing steam over a working building. */
  vents: number;
  /** Haulers drawn, and how many of them are between their ends. */
  haulers: number;
  haulersMoving: number;
  /** The arrays' tracking roll off level, degrees; 0 stowed. */
  trackDeg: number;
  rebuilds: number;
  /** Times the terrain shadow was re-marched and the light attributes repainted, cumulative. */
  sunRepaints: number;
  /**
   * The worst part base above the drawn ground at its own centre, metres:
   * positive is a part standing in the air. And the deepest one below it,
   * which the foundation has to cover.
   */
  floatMaxM: number;
  sinkMaxM: number;
  /** How far the lowest corner of any array deck, turned as drawn, stands under the ground; 0 clear. */
  deckSinkM: number;
}

export interface StructureMeshes {
  readonly group: THREE.Group;
  set(list: readonly StructureEntry[]): void;
  setLooks(looks: ReadonlyMap<string, StructureLook>): void;
  /** The haul runs to drive, and the site pile they may start or end at. */
  setHauls(routes: readonly HaulRoute[], pile: HaulPoint | null): void;
  /** Each hauler as last drawn, off its instance matrix: plot-local position, and whether it was moving and loaded. */
  haulDebug(): Array<{ key: string; x: number; y: number; z: number; moving: boolean; loaded: boolean }>;
  update(camLocal: THREE.Vector3, camera?: THREE.Camera, paneHeightPx?: number): void;
  setCastShadow(on: boolean): void;
  /** The structure a world-space ray meets first, off the drawn parts. */
  pick(ray: THREE.Ray): StructureEntry | null;
  entries(): readonly StructureEntry[];
  stats(): StructureMeshStats;
  dispose(): void;
}

/** The key a structure's look is pushed under. */
export function structureKey(e: { cell?: string; kind?: string }): string {
  return `${e.cell ?? ''}:${e.kind ?? ''}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Parts
// ─────────────────────────────────────────────────────────────────────────────

/** Surface zones a vertex carries; the fragment shader picks a material per zone. */
const Z_HULL = 0;
const Z_COPPER = 1;
const Z_DARK = 2;
const Z_WINDOW = 3;
const Z_STATUS = 4;
const Z_REGOLITH = 5;
const Z_CELL = 6;
const Z_FOUNDATION = 7;
/** Hot metal seen through a furnace port: emissive, and not a window. */
const Z_HOT = 8;
/** A heat-rejection panel: dark, and warm at the root while its building works. */
const Z_RADIATOR = 9;

/** Height of an array row's torque tube over its ground: the deck turns about it. */
const PANEL_PIVOT_M = 2.3;
/** The furthest a tracker turns off level, radians (its deck's edge stays over the ground), and the change that re-turns the decks. */
const TRACK_MAX = 0.95;
const TRACK_STEP = 0.02;
/**
 * Sun-direction slop, radians, below which re-marching a building's terrain
 * shadow would not move its lit edge enough to see: tighter than TRACK_STEP,
 * so a sun-tracking array still catches every turn it would have. At the
 * clock's fastest warp (1 DAY/S, `WARP_LEVELS` in world/clock.ts) the sun's
 * hour angle covers this in a fraction of a millisecond, so every frame still
 * repaints there; at real time it covers it in roughly half a minute.
 */
const SUN_REPAINT_EPS_RAD = 0.002;

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _xax = new THREE.Vector3(1, 0, 0);
const _q2 = new THREE.Quaternion();
const _m2 = new THREE.Matrix4();
const _hf = new THREE.Vector3();
const _hs = new THREE.Vector3();
const _hn = new THREE.Vector3();
const _hx = new THREE.Vector3();

/** A part's triangles, gathered from primitives placed in its own metres. */
class Kit {
  readonly pos: number[] = [];
  readonly nrm: number[] = [];
  readonly zone: number[] = [];

  put(g: THREE.BufferGeometry, zone: number, x: number, y: number, z: number,
      rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1): void {
    const ng = g.index ? g.toNonIndexed() : g;
    _m.compose(_p.set(x, y, z), _q.setFromEuler(_e.set(rx, ry, rz)), _s.set(sx, sy, sz));
    ng.applyMatrix4(_m);
    const p = ng.getAttribute('position');
    const n = ng.getAttribute('normal');
    for (let i = 0; i < p.count; i++) {
      this.pos.push(p.getX(i), p.getY(i), p.getZ(i));
      this.nrm.push(n.getX(i), n.getY(i), n.getZ(i));
      this.zone.push(zone);
    }
    if (ng !== g) ng.dispose();
    g.dispose();
  }

  /** A box standing on `y`, centred on x and z. */
  box(w: number, h: number, d: number, x: number, y: number, z: number, zone: number, ry = 0): void {
    this.put(new THREE.BoxGeometry(w, h, d), zone, x, y + h / 2, z, 0, ry);
  }

  /** An upright cylinder standing on `y`; `rTop` tapers it. */
  cyl(r: number, h: number, x: number, y: number, z: number, zone: number, seg: number, rTop = r): void {
    this.put(new THREE.CylinderGeometry(rTop, r, h, seg), zone, x, y + h / 2, z);
  }

  /** A horizontal cylinder along x (turned by `ry`), its axis at height `y`. */
  hcyl(r: number, len: number, x: number, y: number, z: number, zone: number, seg: number, ry = 0): void {
    this.put(new THREE.CylinderGeometry(r, r, len, seg), zone, x, y, z, 0, ry, Math.PI / 2);
  }

  /**
   * A half cylinder along x, flat side down on `y`: a barrel roof or a berm.
   * `flat` squashes its height against its width.
   */
  vault(r: number, len: number, x: number, y: number, z: number, zone: number, seg: number, flat = 1): void {
    this.put(new THREE.CylinderGeometry(r, r, len, seg, 1, false, 0, Math.PI), zone,
      x, y, z, 0, 0, Math.PI / 2, flat, 1, 1);
  }

  dome(r: number, x: number, y: number, z: number, zone: number, seg: number): void {
    this.put(new THREE.SphereGeometry(r, seg, Math.max(3, seg >> 1), 0, Math.PI * 2, 0, Math.PI / 2), zone, x, y, z);
  }

  sphere(r: number, x: number, y: number, z: number, zone: number, seg: number, sx = 1): void {
    this.put(new THREE.SphereGeometry(r, seg, Math.max(3, seg >> 1)), zone, x, y, z, 0, 0, 0, sx, 1, 1);
  }

  /** A rectangular foundation whose top sits half a metre proud of the base. */
  slab(w: number, d: number, x = 0, z = 0): void {
    this.box(w, 7.5, d, x, -7, z, Z_FOUNDATION);
  }

  roundSlab(r: number, seg: number): void {
    this.cyl(r, 7.5, 0, -7, 0, Z_FOUNDATION, seg);
  }

  build(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nrm, 3));
    g.setAttribute('aZone', new THREE.Float32BufferAttribute(this.zone, 1));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }
}

interface PartDef {
  /** Footprint radius, metres: where the ground under it is sampled. */
  r: number;
  /** Dropped past NEAR. */
  minor: boolean;
  /** Hull tint: pewter, slate or a pale regolith-sintered white. */
  tint: readonly [number, number, number];
  build(k: Kit, seg: number): void;
}

const PEWTER = [0.56, 0.57, 0.56] as const;
const SLATE = [0.40, 0.41, 0.43] as const;
const PALE = [0.70, 0.68, 0.64] as const;

const PARTS = {
  /** A process hall: walls, a flattened barrel roof, lit window strips and a lit bay door. */
  hall: { r: 22, minor: false, tint: PEWTER, build(k, seg) {
    k.slab(44, 26);
    k.box(40, 7, 22, 0, 0.5, 0, Z_HULL);
    k.vault(11, 40, 0, 7.5, 0, Z_HULL, seg * 2, 0.42);
    k.box(40.4, 0.8, 22.4, 0, 6.9, 0, Z_COPPER);
    for (let i = -4; i <= 4; i++) {
      k.box(2.2, 1.2, 0.3, i * 4.2, 3.4, 11.05, Z_WINDOW);
      k.box(2.2, 1.2, 0.3, i * 4.2, 3.4, -11.05, Z_WINDOW);
    }
    k.box(0.4, 5, 7, 20.1, 0.5, 0, Z_WINDOW);
    k.box(0.6, 0.6, 8, 20.2, 5.6, 0, Z_COPPER);
    for (const x of [-12, 0, 12]) k.box(3, 1.6, 3, x, 11, 0, Z_DARK);
    k.cyl(0.18, 4, 17, 11, 4, Z_DARK, 4);
    k.sphere(0.6, 17, 15.3, 4, Z_STATUS, 8);
  } },
  /** An ore hopper on four legs, fed by the conveyor, a mast and the beacon on top. */
  hopper: { r: 8, minor: false, tint: PALE, build(k, seg) {
    k.slab(16, 16);
    for (const x of [-5.5, 5.5]) for (const z of [-5.5, 5.5]) k.box(1.4, 18, 1.4, x, 0.5, z, Z_DARK);
    k.put(new THREE.ConeGeometry(6, 5, seg), Z_HULL, 0, 16, 0, Math.PI);
    k.cyl(6, 10, 0, 18.5, 0, Z_HULL, seg);
    for (const y of [20, 26]) k.cyl(6.12, 0.8, 0, y, 0, Z_COPPER, seg);
    k.put(new THREE.ConeGeometry(6.2, 2, seg), Z_DARK, 0, 29.5, 0);
    k.box(0.4, 9, 0.4, 0, 30.5, 0, Z_DARK);
    k.sphere(0.8, 0, 40, 0, Z_STATUS, 8);
  } },
  /** A covered belt rising toward +z, from the digging face up to the hopper's mouth. */
  conveyor: { r: 30, minor: true, tint: PEWTER, build(k) {
    const rise = Math.atan2(18, 64);
    k.put(new THREE.BoxGeometry(2.6, 1.2, 64), Z_HULL, 0, 12, 0, -rise);
    k.put(new THREE.BoxGeometry(2.8, 0.25, 64.2), Z_COPPER, 0, 12.75, 0, -rise);
    for (const z of [-24, -8, 8, 24]) {
      const yb = 12 + z * (18 / 64) - 0.8;
      k.box(3.2, yb + 4, 0.8, 0, -4, z, Z_DARK);
    }
  } },
  /** A tracked bucket-wheel excavator at the digging face. */
  excavator: { r: 12, minor: true, tint: PALE, build(k, seg) {
    for (const x of [-3.6, 3.6]) k.box(2.2, 2.2, 11, x, 0, 0, Z_DARK);
    k.box(7, 3.5, 8, 0, 2.2, 0, Z_HULL);
    k.box(3, 2.5, 3, 2, 5.7, 1.5, Z_HULL);
    k.box(3.05, 0.9, 0.2, 2, 6.9, 3.05, Z_WINDOW);
    k.put(new THREE.BoxGeometry(1.2, 1.2, 16), Z_COPPER, 0, 5.2, -10, -0.3);
    k.put(new THREE.CylinderGeometry(4.2, 4.2, 1.4, seg), Z_DARK, 0, 4.3, -18.5, 0, 0, Math.PI / 2);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      k.put(new THREE.BoxGeometry(1.8, 1.1, 1.1), Z_COPPER, 0, 4.3 + Math.sin(a) * 4.3, -18.5 + Math.cos(a) * 4.3, a);
    }
    k.sphere(0.45, -2, 6.2, -2, Z_STATUS, 6);
  } },
  /** Spoil heaps: the regolith the excavator has already been through. */
  spoil: { r: 16, minor: false, tint: PEWTER, build(k, seg) {
    k.put(new THREE.ConeGeometry(20, 7, Math.max(9, seg)), Z_REGOLITH, 0, 2.5, 0, 0, 0, 0, 1, 1, 0.75);
    k.put(new THREE.ConeGeometry(11, 5, Math.max(8, seg - 2)), Z_REGOLITH, 14, 1.5, 9);
  } },
  /** A horizontal pressure tank on two saddles. */
  tankH: { r: 9, minor: true, tint: PALE, build(k, seg) {
    k.slab(18, 8);
    for (const x of [-5, 5]) k.box(1.2, 2.8, 6, x, 0.5, 0, Z_DARK);
    k.hcyl(3, 14, 0, 5.5, 0, Z_HULL, seg);
    for (const x of [-7, 7]) k.sphere(3, x, 5.5, 0, Z_HULL, seg, 0.45);
    for (const x of [-3, 3]) k.hcyl(3.1, 0.6, x, 5.5, 0, Z_COPPER, seg);
  } },
  /**
   * A radiator wing: waste heat leaves a vacuum-thin atmosphere by radiating,
   * so every plant here wears a lot of these.
   */
  radiator: { r: 11, minor: true, tint: SLATE, build(k) {
    k.slab(22, 3);
    for (const x of [-7, 7]) k.box(1.5, 2.2, 1.5, x, 0.5, 0, Z_DARK);
    k.box(20, 7, 0.35, 0, 2.6, 0, Z_RADIATOR);
    k.box(20.4, 0.4, 0.5, 0, 9.6, 0, Z_COPPER);
    k.box(20.4, 0.4, 0.5, 0, 2.4, 0, Z_COPPER);
    for (let x = -10; x <= 10; x += 2.5) k.box(0.15, 7, 0.45, x, 2.6, 0, Z_COPPER);
  } },
  /** A distillation or electrolysis column with its platforms. */
  column: { r: 4.5, minor: false, tint: PALE, build(k, seg) {
    k.roundSlab(4.5, seg);
    k.cyl(2.4, 30, 0, 0.5, 0, Z_HULL, seg);
    k.dome(2.4, 0, 30.5, 0, Z_HULL, seg);
    for (const y of [8, 16, 24]) k.cyl(2.5, 0.6, 0, y, 0, Z_COPPER, seg);
    for (const y of [12, 22]) k.cyl(3.6, 0.35, 0, y, 0, Z_DARK, seg);
    k.box(0.2, 2.2, 0.2, 0, 32.5, 0, Z_DARK);
    k.sphere(0.45, 0, 35, 0, Z_STATUS, 6);
  } },
  /** A cryogenic sphere on a skirt and six legs. */
  sphereTank: { r: 8, minor: false, tint: PALE, build(k, seg) {
    k.roundSlab(8, seg);
    k.cyl(4.5, 5, 0, 0.5, 0, Z_DARK, seg);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      k.box(0.8, 9, 0.8, Math.cos(a) * 6, 0.5, Math.sin(a) * 6, Z_DARK, -a);
    }
    k.sphere(7, 0, 11.5, 0, Z_HULL, Math.round(seg * 1.5));
    k.cyl(7.08, 0.7, 0, 11.15, 0, Z_COPPER, Math.round(seg * 1.5));
  } },
  /** A pipe rack carrying three lines across the plant. */
  pipeRack: { r: 30, minor: true, tint: PEWTER, build(k, seg) {
    for (let i = 0; i <= 6; i++) {
      const x = -30 + i * 10;
      for (const z of [-2, 2]) k.box(0.6, 11, 0.6, x, -3, z, Z_DARK);
      k.box(0.6, 0.6, 4.6, x, 7.6, 0, Z_DARK);
    }
    k.hcyl(0.55, 62, 0, 8.8, -1.4, Z_COPPER, Math.max(6, seg >> 1));
    k.hcyl(0.7, 62, 0, 8.95, 0, Z_HULL, Math.max(6, seg >> 1));
    k.hcyl(0.55, 62, 0, 8.8, 1.4, Z_COPPER, Math.max(6, seg >> 1));
  } },
  /** A fabrication vault under a regolith berm, its lit bay doors at both ends. */
  bermHall: { r: 26, minor: false, tint: PEWTER, build(k, seg) {
    k.box(64, 7.5, 16, 0, -7, 0, Z_FOUNDATION);
    k.vault(17, 44, 0, -1, 0, Z_REGOLITH, seg * 2, 0.62);
    for (const s of [-1, 1]) {
      k.vault(13, 5, s * 24.5, 0, 0, Z_HULL, seg * 2, 0.75);
      k.put(new THREE.CylinderGeometry(12.6, 12.6, 0.4, seg * 2, 1, false, 0, Math.PI), Z_DARK,
        s * 27.05, 0, 0, 0, 0, Math.PI / 2, 0.75, 1, 1);
      k.box(0.3, 6.5, 9, s * 27.3, 0, 0, Z_WINDOW);
      k.box(0.5, 0.6, 10, s * 27.4, 6.5, 0, Z_COPPER);
    }
    k.box(30, 0.4, 1.4, 0, 9.3, 0, Z_WINDOW);
  } },
  /** A gantry crane over an open work pad, a hull section hanging under it. */
  gantry: { r: 17, minor: true, tint: PEWTER, build(k) {
    k.box(36, 7.5, 18, 0, -7, 0, Z_FOUNDATION);
    for (const x of [-15, 15]) for (const z of [-6, 6]) k.box(1.2, 22, 1.2, x, 0.5, z, Z_COPPER);
    for (const z of [-6, 6]) k.box(32, 1.6, 1.4, 0, 22.5, z, Z_COPPER);
    for (const x of [-15, 15]) k.box(1.4, 1.4, 13, x, 22.6, 0, Z_COPPER);
    k.box(4, 2.5, 13.5, 3, 20, 0, Z_DARK);
    k.box(0.2, 8, 0.2, 3, 12, 0, Z_DARK);
    k.box(8, 4, 5, 3, 0.5, 0, Z_HULL);
  } },
  /** A pressurised dome on a ring wall, a lit window band and an airlock. */
  domeHab: { r: 11, minor: false, tint: PALE, build(k, seg) {
    k.roundSlab(11, seg);
    k.cyl(9, 2.6, 0, 0.5, 0, Z_HULL, Math.round(seg * 1.5));
    k.dome(9, 0, 3.1, 0, Z_HULL, Math.round(seg * 1.5));
    k.cyl(9.1, 0.5, 0, 2.6, 0, Z_COPPER, Math.round(seg * 1.5));
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      k.box(1.5, 0.9, 0.3, Math.sin(a) * 9.02, 1.3, Math.cos(a) * 9.02, Z_WINDOW, a);
    }
    k.box(4, 3, 5, 0, 0.5, 10.5, Z_HULL);
    k.box(2, 2, 0.2, 0, 0.7, 13.05, Z_WINDOW);
  } },
  /** Six battery modules in a row, each with its terminals and a charge strip. */
  batteryRow: { r: 40, minor: false, tint: SLATE, build(k, seg) {
    k.slab(80, 6);
    for (let i = 0; i < 6; i++) {
      const x = -32.5 + i * 13;
      k.box(12, 2.9, 2.6, x, 0.5, 0, Z_DARK);
      k.box(12.2, 0.25, 2.8, x, 3.4, 0, Z_HULL);
      for (const dx of [-4, 4]) k.cyl(0.25, 0.6, x + dx, 3.65, 0, Z_COPPER, Math.max(5, seg >> 1));
      for (const z of [-1.31, 1.31]) k.box(10, 0.25, 0.1, x, 1.9, z, Z_STATUS);
    }
  } },
  /** A transformer with its cooling fins and copper bushings. */
  transformer: { r: 5, minor: true, tint: SLATE, build(k, seg) {
    k.slab(10, 10);
    k.box(6, 5, 5, 0, 0.5, 0, Z_HULL);
    for (let i = -3; i <= 3; i++) for (const z of [-3.2, 3.2]) k.box(0.15, 4, 1.4, i * 0.9, 1, z, Z_DARK);
    for (const x of [-1.8, 0, 1.8]) {
      k.cyl(0.3, 1.8, x, 5.5, 0, Z_COPPER, Math.max(5, seg >> 1));
      k.cyl(0.55, 0.15, x, 6.2, 0, Z_DARK, Math.max(5, seg >> 1));
    }
  } },
  /** A comms mast: a tapered four-sided tower, a dish, and the beacon at its head. */
  mast: { r: 2.5, minor: false, tint: SLATE, build(k, seg) {
    k.roundSlab(2.5, Math.max(6, seg >> 1));
    k.cyl(1.1, 32, 0, 0.5, 0, Z_DARK, 4, 0.35);
    k.put(new THREE.CylinderGeometry(2.2, 0.2, 0.8, seg), Z_HULL, 0.9, 22, 0, 0, 0, -1.1);
    k.sphere(0.9, 0, 33.4, 0, Z_STATUS, 8);
  } },
  /**
   * An electric smelting hall: a stepped furnace house, three copper
   * electrode columns through its roof, a lit tapping port and an off-gas
   * stack. No combustion - there is nothing to burn in - so the stack vents
   * oxygen and the glow is the melt itself.
   */
  furnace: { r: 16, minor: false, tint: SLATE, build(k, seg) {
    k.slab(34, 24);
    k.box(30, 12, 20, 0, 0.5, 0, Z_HULL);
    k.box(18, 7, 14, -3, 12.5, 0, Z_HULL);
    k.box(30.4, 0.7, 20.4, 0, 12.2, 0, Z_COPPER);
    for (const x of [-8, -3, 2]) {
      k.cyl(0.8, 9, x, 19.5, 0, Z_COPPER, Math.max(6, seg >> 1));
      k.cyl(1.1, 0.5, x, 19.5, 0, Z_DARK, Math.max(6, seg >> 1));
    }
    k.box(6, 3, 0.4, 6, 1.5, 10.1, Z_HOT);
    k.box(7, 0.6, 0.6, 6, 4.6, 10.2, Z_COPPER);
    for (const z of [-6, 0, 6]) k.box(0.3, 1.4, 2.2, 15.05, 7, z, Z_WINDOW);
    k.cyl(1.6, 30, 11, 0.5, -5, Z_DARK, seg, 1.2);
    k.cyl(1.7, 0.8, 11, 26, -5, Z_COPPER, seg);
    k.sphere(0.6, 11, 31.4, -5, Z_STATUS, 8);
  } },
  /** An ingot and billet yard: stacked product on a sintered apron. */
  ingotYard: { r: 12, minor: true, tint: PEWTER, build(k) {
    k.slab(24, 16);
    for (let i = 0; i < 4; i++) for (let j = 0; j < 3; j++) {
      const h = 1.2 + ((i * 7 + j * 3) % 4) * 0.6;
      k.box(3.4, h, 2.2, -8 + i * 5.2, 0.5, -4.5 + j * 4.5, j === 1 ? Z_COPPER : Z_HULL);
    }
  } },
  /**
   * A clean fabrication hall: a long low block under a sawtooth roof whose
   * steep faces are glazed toward the pole, so it is lit without the sun
   * reaching the benches.
   */
  cleanRoom: { r: 22, minor: false, tint: PALE, build(k) {
    k.slab(44, 22);
    k.box(40, 6, 18, 0, 0.5, 0, Z_HULL);
    for (let i = 0; i < 5; i++) {
      const x = -16 + i * 8;
      k.put(new THREE.BoxGeometry(8, 0.4, 18.4), Z_HULL, x - 0.2, 8.3, 0, 0, 0, 0.55);
      k.box(0.3, 3.8, 17.6, x + 3.6, 6.4, 0, Z_WINDOW);
    }
    k.box(40.4, 0.6, 18.4, 0, 6.2, 0, Z_COPPER);
    k.box(6, 4, 0.4, -12, 0.5, 9.1, Z_WINDOW);
    for (const x of [8, 14]) k.box(3, 1.4, 3, x, 10.4, -5, Z_DARK);
  } },
  /**
   * An assembly high bay: a tall hangar with a full-height lit door, a roof
   * crane rail and a clerestory strip.
   */
  highBay: { r: 30, minor: false, tint: PEWTER, build(k, seg) {
    k.slab(60, 40);
    k.box(54, 26, 34, 0, 0.5, 0, Z_HULL);
    k.vault(17, 54, 0, 26.5, 0, Z_HULL, seg * 2, 0.3);
    k.box(54.4, 0.9, 34.4, 0, 25.8, 0, Z_COPPER);
    k.box(0.4, 22, 20, 27.1, 0.5, 0, Z_WINDOW);
    for (const z of [-11, 11]) k.box(1.2, 24, 1.6, 27.4, 0.5, z, Z_COPPER);
    for (const z of [-17.05, 17.05]) k.box(46, 1.2, 0.3, 0, 21, z, Z_WINDOW);
    for (let x = -24; x <= 24; x += 8) for (const z of [-17.1, 17.1]) k.box(1, 26, 0.5, x, 0.5, z, Z_DARK);
    k.cyl(0.18, 5, -22, 31.5, 8, Z_DARK, 4);
    k.sphere(0.7, -22, 37, 8, Z_STATUS, 8);
  } },
  /** A lander on the apron outside the bay: a body, four splayed legs, an engine bell. */
  lander: { r: 9, minor: true, tint: PALE, build(k, seg) {
    k.slab(20, 20);
    k.put(new THREE.ConeGeometry(2.4, 2.4, seg, 1, true), Z_DARK, 0, 2.2, 0, Math.PI);
    k.cyl(3.4, 8, 0, 3.4, 0, Z_HULL, seg, 3.0);
    k.put(new THREE.ConeGeometry(3.0, 3.5, seg), Z_HULL, 0, 13.15, 0);
    k.cyl(3.45, 0.6, 0, 7, 0, Z_COPPER, seg);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      k.put(new THREE.BoxGeometry(0.5, 7, 0.5), Z_DARK, Math.cos(a) * 4.6, 3.2, Math.sin(a) * 4.6,
        Math.sin(a) * 0.45, 0, -Math.cos(a) * 0.45);
      k.cyl(0.9, 0.3, Math.cos(a) * 6.1, 0.4, Math.sin(a) * 6.1, Z_DARK, Math.max(6, seg >> 1));
    }
  } },
  /** One row's posts and torque tube along the row (+x); its deck is `panelDeck`, which turns on the tube. */
  panelRow: { r: 12, minor: false, tint: SLATE, build(k) {
    for (const x of [-10, 0, 10]) k.box(0.25, 3, 0.25, x, -0.8, 0, Z_DARK);
    k.hcyl(0.16, 24.4, 0, PANEL_PIVOT_M, 0, Z_DARK, 6);
  } },
  /**
   * A row's photovoltaic deck, level about its own pivot on the tube (+x);
   * the instance turns it about that axis to follow the sun.
   */
  panelDeck: { r: 12, minor: false, tint: SLATE, build(k) {
    k.put(new THREE.BoxGeometry(24, 0.18, 4.2), Z_CELL, 0, 0, 0);
    k.put(new THREE.BoxGeometry(24.2, 0.14, 0.3), Z_COPPER, 0, 0, 2.1);
  } },
  /**
   * A regolith hauler, nose along +z: six wheels, a cab with a lit screen, a
   * tub and a cab-roof status lamp. True size, about a terrestrial
   * articulated dump truck.
   */
  hauler: { r: 6, minor: false, tint: PALE, build(k, seg) {
    k.box(4.2, 1.1, 11, 0, 1.3, 0, Z_DARK);
    for (const z of [-3.8, 0, 3.8]) for (const x of [-2.25, 2.25]) k.hcyl(1.15, 0.9, x, 1.15, z, Z_DARK, seg);
    k.box(3.8, 2.4, 2.6, 0, 2.4, 3.9, Z_HULL);
    k.box(3.0, 0.9, 0.2, 0, 3.6, 5.22, Z_WINDOW);
    k.box(4.1, 1.9, 6.6, 0, 2.4, -1.9, Z_HULL);
    k.box(4.2, 0.25, 6.7, 0, 4.3, -1.9, Z_COPPER);
    k.sphere(0.32, 0, 5.1, 3.9, Z_STATUS, 6);
  } },
  /** The heaped load a hauler's tub carries on its loaded leg. */
  haulLoad: { r: 3, minor: false, tint: PEWTER, build(k, seg) {
    k.put(new THREE.SphereGeometry(1, seg, Math.max(3, seg >> 1), 0, Math.PI * 2, 0, Math.PI / 2),
      Z_REGOLITH, 0, 4.2, -1.9, 0, 0, 0, 1.9, 1.1, 3.1);
  } },
} satisfies Record<string, PartDef>;

type PartId = keyof typeof PARTS;

// ─────────────────────────────────────────────────────────────────────────────
// Designs
// ─────────────────────────────────────────────────────────────────────────────

/** A part placed in a compound: metres off the compound's centre, turned by `rot`. */
interface PartUse { part: PartId; x: number; z: number; rot?: number; s?: number }

interface Design {
  parts: PartUse[];
  /** Where the job beacon hangs: x, height over the ground there, z. */
  beacon: readonly [number, number, number];
  /** Where an active extraction raises dust: x, z. */
  plume?: readonly [number, number];
}

const QUARTER = Math.PI / 2;
/**
 * Parts are authored small and drawn at 1.6x: a hall 64 m by 35 m, a hopper
 * 65 m tall, cryo spheres 22 m across, radiator wings 32 m long. That is the
 * size of a terrestrial ISRU or chemical plant's process buildings (a
 * cryogenic air-separation unit's cold box runs 40-60 m, a LOX sphere
 * 15-25 m), and it is what a 300 m pad needs so it does not read as litter.
 */
const PART_SCALE = 1.6;

/**
 * One compound per class, laid out for the 150 m minimum footprint
 * (`MIN_FORGE_HALF_M`, siteGround.ts) and spread with a bigger one. A class
 * the table does not know - a smelter or a fabricator added to the content
 * after this - draws `generic`, a hab and a hall, rather than nothing.
 */
const DESIGNS: Record<string, Design> = {
  extractor: {
    parts: [
      { part: 'hall', x: 0, z: 34 },
      { part: 'hopper', x: 0, z: -8 },
      { part: 'conveyor', x: 0, z: -46 },
      { part: 'excavator', x: 0, z: -84 },
      { part: 'spoil', x: -44, z: -66 },
      { part: 'spoil', x: 42, z: -84, rot: 2.1, s: 0.7 },
      { part: 'tankH', x: 40, z: 24, rot: QUARTER },
      { part: 'tankH', x: 52, z: 24, rot: QUARTER },
      { part: 'radiator', x: -40, z: 22, rot: QUARTER },
      { part: 'radiator', x: -50, z: 22, rot: QUARTER },
    ],
    beacon: [0, 40.8, -8],
    plume: [0, -102],
  },
  refinery: {
    parts: [
      { part: 'hall', x: 0, z: 46, s: 0.8 },
      { part: 'column', x: -22, z: -8 },
      { part: 'column', x: -8, z: -20, s: 0.85 },
      { part: 'column', x: -28, z: -30, s: 1.15 },
      { part: 'sphereTank', x: 28, z: -18 },
      { part: 'sphereTank', x: 48, z: 4, s: 0.85 },
      { part: 'pipeRack', x: 0, z: 12 },
      { part: 'radiator', x: -72, z: 34, rot: QUARTER },
      { part: 'radiator', x: -64, z: 34, rot: QUARTER },
      { part: 'radiator', x: -56, z: 34, rot: QUARTER },
      { part: 'mast', x: 54, z: 46 },
    ],
    beacon: [54, 34.2, 46],
  },
  workshop: {
    parts: [
      { part: 'bermHall', x: 0, z: 0 },
      { part: 'gantry', x: 0, z: -48 },
      { part: 'domeHab', x: -52, z: 40 },
      { part: 'domeHab', x: -26, z: 58, s: 0.8 },
      { part: 'radiator', x: 52, z: 34, rot: QUARTER },
      { part: 'radiator', x: 60, z: 34, rot: QUARTER },
      { part: 'tankH', x: 44, z: -40 },
      { part: 'mast', x: 60, z: -18 },
    ],
    beacon: [60, 34.2, -18],
  },
  battery: {
    parts: [
      { part: 'batteryRow', x: 0, z: -36 },
      { part: 'batteryRow', x: 0, z: -22 },
      { part: 'batteryRow', x: 0, z: -8 },
      { part: 'batteryRow', x: 0, z: 6 },
      { part: 'transformer', x: 50, z: 34 },
      { part: 'transformer', x: 36, z: 34, s: 0.8 },
      { part: 'radiator', x: -8, z: 34 },
      { part: 'mast', x: -52, z: 36 },
    ],
    beacon: [-52, 34.2, 36],
  },
  smelter: {
    parts: [
      { part: 'furnace', x: 0, z: 0 },
      { part: 'furnace', x: 0, z: -34, s: 0.8 },
      { part: 'ingotYard', x: 38, z: 14 },
      { part: 'spoil', x: -48, z: -52, rot: 1.2, s: 0.55 },
      { part: 'tankH', x: 36, z: -26, rot: QUARTER },
      { part: 'radiator', x: -44, z: 20, rot: QUARTER },
      { part: 'radiator', x: -52, z: 20, rot: QUARTER },
      { part: 'radiator', x: -60, z: 20, rot: QUARTER },
      { part: 'mast', x: 50, z: 46 },
    ],
    beacon: [50, 34.2, 46],
  },
  fabricator: {
    parts: [
      { part: 'cleanRoom', x: 0, z: 0 },
      { part: 'cleanRoom', x: 0, z: 30, s: 0.85 },
      { part: 'domeHab', x: -46, z: -30 },
      { part: 'column', x: 36, z: -30, s: 0.55 },
      { part: 'column', x: 44, z: -30, s: 0.55 },
      { part: 'transformer', x: 44, z: 12 },
      { part: 'radiator', x: -48, z: 26, rot: QUARTER },
      { part: 'radiator', x: -56, z: 26, rot: QUARTER },
      { part: 'mast', x: 50, z: 50 },
    ],
    beacon: [50, 34.2, 50],
  },
  assembler: {
    parts: [
      { part: 'highBay', x: 0, z: 0 },
      { part: 'gantry', x: 58, z: -30, rot: QUARTER },
      { part: 'lander', x: 62, z: 22 },
      { part: 'tankH', x: -40, z: -34 },
      { part: 'radiator', x: -48, z: 30, rot: QUARTER },
      { part: 'radiator', x: -56, z: 30, rot: QUARTER },
      { part: 'domeHab', x: -20, z: 44, s: 0.8 },
    ],
    beacon: [-22, 37, 8],
  },
  generic: {
    parts: [
      { part: 'domeHab', x: 0, z: 0 },
      { part: 'hall', x: 44, z: 12, s: 0.7 },
      { part: 'radiator', x: 0, z: 36 },
      { part: 'mast', x: -32, z: -22 },
    ],
    beacon: [-32, 34.2, -22],
  },
};

/** The compound a kind draws. Exported for the test of its fallback. */
export function designKindFor(kind: string | undefined): string {
  return kind && Object.prototype.hasOwnProperty.call(DESIGNS, kind) ? kind : 'generic';
}

/** Panel rows are laid on this pitch, and cut to this many at most. */
const PANEL_PITCH_M = 9;
const PANEL_SEG_M = 26;
const PANEL_CAP = 900;

/** Range, metres, past which a building drops to the next tier; 10% hysteresis back. */
const NEAR_M = 1800;
const MID_M = 6000;
const HYST = 1.1;

/** A beacon never draws smaller than this on the pane. */
const BEACON_MIN_PX = 7;
const PUFFS_PER_PLUME = 10;

/**
 * Where a part breathes while its building works, part-local metres before
 * scale: 1 is steam off a vent, 2 the haze over a radiator's top.
 */
const VENTS: Partial<Record<PartId, ReadonlyArray<readonly [number, number, number, number]>>> = {
  hall: [[-12, 12.6, 0, 1], [12, 12.6, 0, 1]],
  furnace: [[11, 31.2, -5, 1]],
  radiator: [[-5, 10.2, 0, 2], [5, 10.2, 0, 2]],
};
const PUFFS_PER_VENT = 6;
/** A hauler's lamps, its own metres: two headlights (4, lit after dusk) and the cab flasher (2, while it drives). */
const HAUL_LAMPS: ReadonlyArray<readonly [number, number, number, number]> = [
  [-1.3, 2.2, 5.6, 4], [1.3, 2.2, 5.6, 4], [0, 5.5, 3.9, 2],
];
const PLUME_CAP = 8 * PUFFS_PER_PLUME + 48 * PUFFS_PER_VENT;
/** Haulers are true size, a touch over so one reads from the working height. */
const HAUL_SCALE = 1.3;
/** Haulers draw inside this range of the eye, the buildings' MID band. */
const HAUL_DRAW_M = 6000;
/** The site pile's parts, metres off its spot. */
const PILE_PARTS: ReadonlyArray<{ part: PartId; x: number; z: number; s: number }> = [
  { part: 'spoil', x: 0, z: 0, s: 1.3 },
  { part: 'spoil', x: 34, z: 22, s: 0.8 },
  { part: 'mast', x: -30, z: -14, s: 0.8 },
];

/** An array, not a building: the kind says so, or class 0 where no kind came with it. */
function isArray(e: StructureEntry): boolean {
  return e.kind !== undefined ? e.kind === 'solar' : (e.classId ?? 0) === 0;
}

/** How far a compound's layout opens out on a pad bigger than the 150 m minimum. */
function spreadOf(e: StructureEntry): number {
  return Math.min(1.6, Math.max(1, e.halfM / 150)) * 1.15;
}

const DEFAULT_ACCENT: readonly [number, number, number] = [0.85, 0.82, 0.74];

function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 100000) / 100000;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shaders
// ─────────────────────────────────────────────────────────────────────────────

/** 0 idle, 1 active, 2 paused, 3 ready: the order `aLight.w` carries. */
const MODE_CODE: Record<StructureMode, number> = { idle: 0, active: 1, paused: 2, ready: 3 };

const NOISE_GLSL = /* glsl */ `
float stH(vec2 p){ p = fract(p * vec2(0.1031, 0.1030)); p += dot(p, p.yx + 33.33); return fract((p.x + p.y) * p.x); }
float stN(vec2 p){
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(stH(i), stH(i + vec2(1,0)), f.x), mix(stH(i + vec2(0,1)), stH(i + vec2(1,1)), f.x), f.y);
}
float stTri(vec3 w, vec3 n, float s){
  vec3 b = pow(abs(n), vec3(4.0)); b /= (b.x + b.y + b.z);
  return stN(w.yz * s) * b.x + stN(w.xz * s) * b.y + stN(w.xy * s) * b.z;
}
`;

/** The status light's level per mode, pulsing while a job runs. */
const STATUS_GLSL = /* glsl */ `
vec3 stStatus(vec4 light, float phase, float t){
  float mode = light.w;
  if (mode < 0.5) return light.rgb * 0.55;
  if (mode < 1.5) return light.rgb * (1.3 + 1.1 * sin(t * 3.2 + phase * 6.2831));
  if (mode < 2.5) return vec3(1.0, 0.55, 0.12) * (step(0.5, fract(t * 0.6 + phase)) * 1.6 + 0.2);
  return light.rgb * 2.0;
}
`;

function partMaterial(u: Record<string, THREE.IUniform>, csmGlsl: string | null): THREE.ShaderMaterial {
  const csm = csmGlsl !== null;
  const uniforms: Record<string, THREE.IUniform> = {
    uSunDir: u['uSunDir']!, uSunFlux: u['uSunFlux']!, uSunCol: u['uSunCol']!, uSkyCol: u['uSkyCol']!,
    uBounceCol: u['uBounceCol']!, uCamLocal: u['uCamLocal']!, uTime: u['uTime']!,
  };
  if (csm) {
    for (const k of ['uCsmMap0', 'uCsmMap1', 'uCsmMap2', 'uCsmMatrix0', 'uCsmMatrix1', 'uCsmMatrix2',
      'uCsmSplits', 'uCsmAnchor', 'uCsmOn', 'uCsmTexel']) {
      if (u[k]) uniforms[k] = u[k];
    }
  }
  return new THREE.ShaderMaterial({
    uniforms,
    vertexShader: /* glsl */ `
      attribute float aZone;
      attribute vec3 aTint;
      attribute vec4 aLight;
      attribute vec4 aMisc;
      varying vec3 vW;
      varying vec3 vN;
      varying vec3 vLocal;
      varying float vZone;
      varying vec3 vTint;
      varying vec4 vLight;
      varying vec4 vMisc;
      ${csm ? 'varying vec3 vWorldPos;' : ''}
      #include <logdepthbuf_pars_vertex>
      bool isPerspectiveMatrix(mat4 m) { return m[2][3] == -1.0; }
      void main(){
        vec4 p = instanceMatrix * vec4(position, 1.0);
        vN = normalize(mat3(instanceMatrix) * normal);
        vW = p.xyz;
        vLocal = position;
        vZone = aZone;
        vTint = aTint;
        vLight = aLight;
        vMisc = aMisc;
        ${csm ? 'vWorldPos = (modelMatrix * p).xyz;' : ''}
        gl_Position = projectionMatrix * modelViewMatrix * p;
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform vec3 uSunDir, uSunCol, uSkyCol, uBounceCol, uCamLocal;
      uniform float uSunFlux, uTime;
      varying vec3 vW;
      varying vec3 vN;
      varying vec3 vLocal;
      varying float vZone;
      varying vec3 vTint;
      varying vec4 vLight;
      varying vec4 vMisc;
      ${csm ? `varying vec3 vWorldPos;\n${csmGlsl}` : ''}
      ${NOISE_GLSL}
      ${STATUS_GLSL}
      #include <logdepthbuf_pars_fragment>
      void main(){
        #include <logdepthbuf_fragment>
        vec3 N = normalize(vN);
        int zone = int(vZone + 0.5);
        float dist = length(uCamLocal - vW);
        // Past dusk the lit things carry the frame: windows, lamps, hot metal.
        float night = 1.0 - smoothstep(-0.06, 0.14, uSunDir.y);
        // Seams and grain fade before they alias: a 2.4 m panel seam is a
        // pixel at a few hundred metres.
        float fine = 1.0 - smoothstep(120.0, 700.0, dist);
        float n1 = stTri(vW, N, 0.35);
        float n2 = stTri(vW, N, 2.7);
        vec3 albedo;
        float spec = 0.2;
        float shin = 20.0;
        vec3 emit = vec3(0.0);
        if (zone == 0) {
          albedo = vTint * (0.88 + 0.22 * n1);
          // Cladding panels on the part's own axes, so the seams follow it.
          vec3 q = abs(fract(vLocal / vec3(2.4, 1.8, 2.4)) - 0.5);
          vec3 fw = max(fwidth(vLocal / vec3(2.4, 1.8, 2.4)), vec3(1e-4));
          vec3 ln = 1.0 - smoothstep(vec3(0.0), fw * 1.5, 0.5 - q);
          float seam = max(max(ln.x * (1.0 - abs(N.x)), ln.y * (1.0 - abs(N.y))), ln.z * (1.0 - abs(N.z)));
          albedo *= 1.0 - 0.22 * seam * fine;
          spec = 0.28; shin = 28.0;
        } else if (zone == 1) {
          // Copper that has sat in CO2 and dust: warm metal breaking into verdigris.
          albedo = mix(vec3(0.52, 0.28, 0.15), vec3(0.24, 0.38, 0.32), smoothstep(0.42, 0.78, n1));
          spec = 0.6; shin = 42.0;
        } else if (zone == 2) {
          albedo = vec3(0.075, 0.08, 0.085) * (0.9 + 0.2 * n2);
          spec = 0.35; shin = 30.0;
        } else if (zone == 3) {
          albedo = vec3(0.04);
          float flick = 0.8 + 0.4 * stH(floor(vW.xz * 0.45) + floor(vW.y * 0.8));
          emit = vec3(1.0, 0.70, 0.40) * 2.1 * vMisc.z * flick * (1.0 + 1.8 * night);
        } else if (zone == 4) {
          albedo = vec3(0.04);
          emit = stStatus(vLight, vMisc.y, uTime) * (1.0 + night);
        } else if (zone == 5) {
          albedo = vec3(0.42, 0.29, 0.20) * (0.78 + 0.36 * n1 + 0.1 * n2);
          spec = 0.0;
        } else if (zone == 6) {
          albedo = vec3(0.03, 0.04, 0.065);
          vec2 cg = abs(fract(vLocal.xz / vec2(1.0, 0.7)) - 0.5);
          vec2 cf = max(fwidth(vLocal.xz / vec2(1.0, 0.7)), vec2(1e-4));
          float cell = max(1.0 - smoothstep(0.0, cf.x * 1.5, 0.5 - cg.x), 1.0 - smoothstep(0.0, cf.y * 1.5, 0.5 - cg.y));
          albedo = mix(albedo, vec3(0.30, 0.31, 0.33), cell * 0.5 * fine);
          spec = 1.1; shin = 90.0;
        } else if (zone == 8) {
          albedo = vec3(0.04);
          emit = vec3(1.0, 0.40, 0.10) * (2.6 + 0.8 * sin(uTime * 5.3 + vW.x * 0.7) * 0.5);
        } else if (zone == 9) {
          albedo = vec3(0.075, 0.08, 0.085) * (0.9 + 0.2 * n2);
          spec = 0.45; shin = 36.0;
          // Heat going out while the building works: warmest at the root, and
          // wavering the way the air over it would.
          float hot = step(1.1, vMisc.z);
          float root = 1.0 - smoothstep(0.0, 7.0, vLocal.y - 2.6);
          float waver = 0.7 + 0.3 * sin(uTime * 2.3 + vW.y * 1.7 + stN(vW.xz * 0.6 + uTime * 0.5) * 6.0);
          emit = vec3(1.0, 0.36, 0.12) * hot * (0.12 + 0.55 * root) * waver * (0.3 + 0.7 * night);
        } else {
          albedo = vec3(0.35, 0.29, 0.24) * (0.82 + 0.3 * n2);
          spec = 0.05;
        }
        if (zone != 3 && zone != 4 && zone != 5 && zone != 8) {
          // Dust settles on anything facing up, and grime climbs a wall from
          // the ground it stands in.
          float up = smoothstep(0.25, 0.95, N.y);
          albedo = mix(albedo, vec3(0.40, 0.28, 0.20), up * (0.25 + 0.35 * n1));
          float foot = 1.0 - smoothstep(0.0, 4.0, vLocal.y);
          albedo = mix(albedo, vec3(0.34, 0.23, 0.17), foot * (1.0 - up) * 0.5);
        }
        // Contact occlusion: a wall's foot sees less sky than its top, and a
        // slab's flank stands in the regolith it was cut from. Heights are the
        // part's own; the foundation's top sits at 0.5.
        float ao = zone == 7 ? mix(0.6, 1.0, smoothstep(0.6, 0.95, N.y))
                             : mix(0.42, 1.0, smoothstep(0.3, 5.0, vLocal.y));
        float ci = max(dot(N, uSunDir), 0.0);
        float sunVis = vMisc.x;
        ${csm ? 'sunVis = min(sunVis, csmVisibility(vWorldPos, length(uCsmAnchor - vWorldPos)));' : ''}
        vec3 V = normalize(uCamLocal - vW);
        vec3 H = normalize(uSunDir + V);
        float sp = pow(max(dot(N, H), 0.0), shin) * spec * (shin + 8.0) / 28.0 * step(0.0, ci);
        vec3 sun = uSunCol * uSunFlux * sunVis;
        vec3 col = albedo * ci * sun * mix(0.8, 1.0, ao) + sun * sp * ci;
        col += albedo * uSkyCol * (0.38 + 0.62 * max(N.y, 0.0)) * ao;
        col += albedo * uBounceCol * (0.35 + 0.65 * (0.5 - 0.5 * N.y)) * uSunFlux * ao;
        // After dusk the floodlights that pool on the pad light the walls'
        // feet, and starlight is just enough to hold a silhouette.
        col += albedo * night * (vec3(1.0, 0.72, 0.45) * (zone == 5 ? 0.22 : 0.5) * (1.0 - smoothstep(0.0, 20.0, vLocal.y)) * ao * (1.0 - vMisc.w)
          + vec3(0.02, 0.024, 0.032));
        col += emit;
        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
}

/** Screen-facing quads: `aSize` metres, never under `uMinPxK` metres per metre of range. */
const BILLBOARD_VERT = /* glsl */ `
  uniform float uMinPxK;
  uniform float uTime;
  attribute vec4 aLight;
  attribute vec2 aPhase;
  varying vec2 vUv;
  varying vec4 vLight;
  varying float vAlpha;
  varying float vPhase;
  #include <logdepthbuf_pars_vertex>
  bool isPerspectiveMatrix(mat4 m) { return m[2][3] == -1.0; }
  void main(){
    vec3 c = (instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    float size = aPhase.y;
    vAlpha = 1.0;
    #ifdef PLUME
      if (aLight.w > 1.5) {
        // Haze over a radiator: small, quick, nearly clear.
        float t = fract(uTime * 0.32 + aPhase.x);
        c += vec3((aPhase.x - 0.5) * 8.0, t * 9.0, 0.0);
        size = 3.0 + t * 5.0;
        vAlpha = smoothstep(0.0, 0.2, t) * (1.0 - t) * 0.35;
      } else if (aLight.w > 0.5) {
        // Steam off a vent: rising fast, leaning downwind, gone in a few seconds.
        float t = fract(uTime * 0.16 + aPhase.x);
        c += vec3(0.35, 0.0, 0.25) * t * 12.0 + vec3(0.0, t * 24.0, 0.0);
        size = 2.5 + t * 12.0;
        vAlpha = smoothstep(0.0, 0.08, t) * pow(1.0 - t, 2.0);
      } else {
        float t = fract(uTime * 0.085 + aPhase.x);
        c += vec3(0.8, 0.0, 0.6) * t * 38.0 + vec3(0.0, 2.0 + t * 30.0, 0.0);
        size = 5.0 + t * 24.0;
        vAlpha = smoothstep(0.0, 0.12, t) * pow(1.0 - t, 1.6);
      }
    #endif
    vec4 mv = modelViewMatrix * vec4(c, 1.0);
    size = max(size, -mv.z * uMinPxK);
    mv.xy += position.xy * size;
    vUv = uv;
    vLight = aLight;
    vPhase = aPhase.x;
    gl_Position = projectionMatrix * mv;
    #include <logdepthbuf_vertex>
  }`;

function glowMaterial(u: Record<string, THREE.IUniform>, minPxK: THREE.IUniform): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: u['uTime']!, uMinPxK: minPxK, uSunDir: u['uSunDir']! },
    vertexShader: BILLBOARD_VERT,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uSunDir;
      varying vec2 vUv;
      varying vec4 vLight;
      varying float vAlpha;
      varying float vPhase;
      ${STATUS_GLSL}
      #include <logdepthbuf_pars_fragment>
      void main(){
        #include <logdepthbuf_fragment>
        vec2 d = vUv * 2.0 - 1.0;
        float r2 = dot(d, d);
        if (r2 > 1.0) discard;
        float core = exp(-r2 * 22.0);
        float halo = exp(-r2 * 4.0) * 0.3;
        float night = 1.0 - smoothstep(-0.06, 0.14, uSunDir.y);
        // Mode 4 is a work lamp: a hauler's headlight, lit only after dusk.
        vec3 c = vLight.w > 3.5
          ? vLight.rgb * night * (core * 2.4 + halo * 2.0)
          : stStatus(vLight, vPhase, uTime) * (core * 2.2 + halo * (1.0 + 2.2 * night));
        gl_FragColor = vec4(c, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
}

function plumeMaterial(u: Record<string, THREE.IUniform>, minPxK: THREE.IUniform): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: u['uTime']!, uMinPxK: minPxK, uSunDir: u['uSunDir']!, uSunFlux: u['uSunFlux']!,
      uSunCol: u['uSunCol']!, uSkyCol: u['uSkyCol']!,
    },
    defines: { PLUME: 1 },
    vertexShader: BILLBOARD_VERT,
    fragmentShader: /* glsl */ `
      uniform vec3 uSunDir, uSunCol, uSkyCol;
      uniform float uSunFlux;
      varying vec2 vUv;
      varying vec4 vLight;
      varying float vAlpha;
      varying float vPhase;
      ${NOISE_GLSL}
      #include <logdepthbuf_pars_fragment>
      void main(){
        #include <logdepthbuf_fragment>
        vec2 d = vUv * 2.0 - 1.0;
        float r = length(d);
        float n = stN(vUv * 5.0 + vPhase * 37.0);
        bool steam = vLight.w > 0.5;
        float a = vAlpha * smoothstep(1.0, 0.15, r + (n - 0.5) * 0.35) * (steam ? 0.42 : 0.34);
        if (a < 0.004) discard;
        vec3 lit = uSunCol * uSunFlux * (0.35 + 0.65 * max(uSunDir.y, 0.0)) + uSkyCol;
        // After dark the plant's own lamps light its steam from below.
        float night = 1.0 - smoothstep(-0.06, 0.14, uSunDir.y);
        vec3 base = steam ? vec3(0.88, 0.86, 0.82) * lit + vec3(0.30, 0.19, 0.09) * night : vec3(0.50, 0.35, 0.24) * lit;
        gl_FragColor = vec4(base, a);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
    transparent: true,
    depthWrite: false,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// The handle
// ─────────────────────────────────────────────────────────────────────────────

/** One part type at one level of detail: its geometry, its mesh, which building each instance is. */
interface Slot {
  geo: THREE.BufferGeometry;
  mesh: THREE.InstancedMesh;
  cap: number;
  owners: Int32Array;
  tint: THREE.InstancedBufferAttribute;
  light: THREE.InstancedBufferAttribute;
  misc: THREE.InstancedBufferAttribute;
}

/** One part instance to draw, resolved against the ground. */
interface Placed { part: PartId; m: THREE.Matrix4; owner: number }

export function createStructureMeshes(opts: StructureMeshesOpts): StructureMeshes {
  const group = new THREE.Group();
  group.name = 'structures';
  /**
   * A row's own +x (its tube) laid along the plot's z, north-south: a
   * single-axis tracker, whose decks face east in the morning, lie flat at
   * noon and face west in the afternoon. The turn takes the row's +z to the
   * plot's +x, which is east.
   */
  const ROW_YAW = Math.PI / 2;
  const u = opts.uniforms;
  const material = partMaterial(u, opts.csmGlsl);
  const minPxK: THREE.IUniform = { value: 0 };
  const glowMat = glowMaterial(u, minPxK);
  const plumeMat = plumeMaterial(u, minPxK);
  const slots = new Map<string, Slot>();
  let list: StructureEntry[] = [];
  let looks: ReadonlyMap<string, StructureLook> = new Map();
  let tiers: StructureTier[] = [];
  /** Terrain sun visibility per building, re-marched when the sun moves. */
  let terrainSun: number[] = [];
  let castOn = false;
  let dirty = true;
  let lastExag = NaN;
  /** The sun direction the terrain shadow was last re-marched from, not merely last seen. */
  const lastPaintedSun = new THREE.Vector3(NaN, NaN, NaN);
  let rebuilds = 0;
  let sunRepaints = 0;
  const glow = billboards(glowMat, 16, 900);
  const plume = billboards(plumeMat, PLUME_CAP, 890);
  const haulLamps = billboards(glowMat, HAUL_CAP * 3, 901);
  haulLamps.mesh.name = 'structure-haul-lamps';
  let dustPlumes = 0;
  let vents = 0;
  let routes: readonly HaulRoute[] = [];
  let pile: HaulPoint | null = null;
  let track = 0;
  let haulersDrawn = 0;
  let haulersMoving = 0;
  const haulBody = soloMesh('hauler', HAUL_CAP);
  const haulLoad = soloMesh('haulLoad', HAUL_CAP);
  const haulKeys: string[] = [];
  const haulMoving = new Uint8Array(HAUL_CAP);
  const haulLoaded = new Uint8Array(HAUL_CAP);
  const lastCam = new THREE.Vector3();
  let pileBand = -1;

  function billboards(mat: THREE.ShaderMaterial, cap: number, order: number) {
    const geo = new THREE.PlaneGeometry(1, 1);
    const light = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    const phase = new THREE.InstancedBufferAttribute(new Float32Array(cap * 2), 2);
    geo.setAttribute('aLight', light);
    geo.setAttribute('aPhase', phase);
    const mesh = new THREE.InstancedMesh(geo, mat, cap);
    mesh.count = 0;
    mesh.visible = false;
    mesh.frustumCulled = false;
    mesh.renderOrder = order;
    mesh.name = mat.defines?.['PLUME'] ? 'structure-plumes' : 'structure-beacons';
    group.add(mesh);
    return { geo, mesh, light, phase, cap };
  }

  /** A part's mesh outside the per-building slots, moved every frame: the haulers. */
  function soloMesh(part: PartId, cap: number): Slot {
    const k = new Kit();
    PARTS[part].build(k, 10);
    const geo = k.build();
    const tint = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    const light = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    const misc = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    geo.setAttribute('aTint', tint);
    geo.setAttribute('aLight', light);
    geo.setAttribute('aMisc', misc);
    const mesh = new THREE.InstancedMesh(geo, material, cap);
    mesh.name = `structure-${part}`;
    mesh.count = 0;
    mesh.visible = false;
    mesh.frustumCulled = false;
    group.add(mesh);
    return { geo, mesh, cap, owners: new Int32Array(cap), tint, light, misc };
  }

  function slotFor(part: PartId, lod: 'hi' | 'lo', need: number): Slot {
    const key = `${part}:${lod}`;
    const had = slots.get(key);
    if (had && had.cap >= need) return had;
    if (had) {
      group.remove(had.mesh);
      had.mesh.dispose();
      had.geo.dispose();
    }
    const k = new Kit();
    PARTS[part].build(k, lod === 'hi' ? 18 : 7);
    const geo = k.build();
    const cap = Math.max(8, need * 2);
    const tint = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    const light = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    const misc = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    geo.setAttribute('aTint', tint);
    geo.setAttribute('aLight', light);
    geo.setAttribute('aMisc', misc);
    const mesh = new THREE.InstancedMesh(geo, material, cap);
    mesh.name = `structure-${key}`;
    mesh.count = 0;
    mesh.visible = false;
    mesh.castShadow = castOn && lod === 'hi';
    group.add(mesh);
    const slot: Slot = { geo, mesh, cap, owners: new Int32Array(cap), tint, light, misc };
    slots.set(key, slot);
    return slot;
  }

  /** How far the building is from the eye, and so which tier it draws at. */
  function tierOf(i: number, e: StructureEntry, cam: THREE.Vector3): StructureTier {
    const y = opts.heightAt(e.x, e.z);
    const d = Math.hypot(cam.x - e.x, cam.y - y, cam.z - e.z);
    const was = tiers[i];
    const near = was === 'near' ? NEAR_M * HYST : NEAR_M;
    const mid = was === 'far' ? MID_M : MID_M * HYST;
    return d < near ? 'near' : d < mid ? 'mid' : 'far';
  }

  /**
   * The building's own heading: its long axis along the contour, which is how
   * a plant goes onto a slope, snapped to 30 degrees; on flat ground a
   * heading off the cell, so two neighbours do not stand in lockstep.
   */
  function yawOf(e: StructureEntry): number {
    const d = 25;
    const gx = opts.heightAt(e.x + d, e.z) - opts.heightAt(e.x - d, e.z);
    const gz = opts.heightAt(e.x, e.z + d) - opts.heightAt(e.x, e.z - d);
    const step = Math.PI / 6;
    if (Math.hypot(gx, gz) / (2 * d) < 0.02) return Math.floor(hash01(e.cell ?? `${e.x},${e.z}`) * 6) * (Math.PI / 3);
    return Math.round(Math.atan2(gx, gz) / step) * step;
  }

  /** The lowest drawn ground under a part's footprint, so no corner floats. */
  function groundUnder(x: number, z: number, r: number): number {
    let y = opts.heightAt(x, z);
    for (const [dx, dz] of [[r, 0], [-r, 0], [0, r], [0, -r]] as const) {
      y = Math.min(y, opts.heightAt(x + dx * 0.8, z + dz * 0.8));
    }
    return y - 0.15;
  }

  function placeCompound(i: number, e: StructureEntry, out: Placed[], minorToo: boolean): void {
    const design = DESIGNS[designKindFor(e.kind)]!;
    const spread = spreadOf(e);
    const yaw = yawOf(e);
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    for (const use of design.parts) {
      const def: PartDef = PARTS[use.part];
      if (def.minor && !minorToo) continue;
      const lx = use.x * spread;
      const lz = use.z * spread;
      const x = e.x + lx * c + lz * s;
      const z = e.z - lx * s + lz * c;
      const sc = (use.s ?? 1) * PART_SCALE;
      const y = groundUnder(x, z, def.r * sc);
      const m = new THREE.Matrix4().compose(
        _p.set(x, y, z), _q.setFromAxisAngle(_up, yaw + (use.rot ?? 0)), _s.set(sc, sc, sc));
      out.push({ part: use.part, m, owner: i });
    }
  }

  /** Panel rows over an array's own hexagon, their tubes north-south. */
  function placeArray(i: number, e: StructureEntry, out: Placed[]): void {
    const R = e.halfM;
    const apo = R * 0.866025;
    let pitch = PANEL_PITCH_M;
    const area = 2.598 * R * R;
    if (area / (pitch * PANEL_SEG_M) > PANEL_CAP) pitch = area / (PANEL_CAP * PANEL_SEG_M);
    _q2.setFromAxisAngle(_up, ROW_YAW).multiply(_q.setFromAxisAngle(_xax, track));
    // Rows run north-south (z) on `pitch` centres across x, cut into PANEL_SEG_M segments.
    for (let dx = -apo + pitch / 2; dx < apo; dx += pitch) {
      for (let dz = -R + PANEL_SEG_M / 2; dz < R; dz += PANEL_SEG_M) {
        // The shader's own hex, pointy along z: vertices at (0, +-R), flat edges at x = +-apothem.
        const ax = Math.abs(dx) + 2.2;
        const az = Math.abs(dz) + PANEL_SEG_M * 0.45;
        if (Math.max(az * 0.866025 + ax * 0.5, ax) > apo) continue;
        const x = e.x + dx;
        const z = e.z + dz;
        const y = groundUnder(x, z, 10);
        const m = new THREE.Matrix4().compose(_p.set(x, y, z), _q.setFromAxisAngle(_up, ROW_YAW), _s.set(1, 1, 1));
        out.push({ part: 'panelRow', m, owner: i });
        const d = new THREE.Matrix4().compose(_p.set(x, y + PANEL_PIVOT_M, z), _q2, _s.set(1, 1, 1));
        out.push({ part: 'panelDeck', m: d, owner: i });
      }
    }
  }

  /**
   * The terrain's shadow at a building: the surface shader's own march, run
   * once on the CPU off the drawn heights, from a point partway up the parts.
   */
  function marchSun(e: StructureEntry): number {
    const sun = u['uSunDir']!.value as THREE.Vector3;
    if (sun.y <= 0) return 0;
    const len = Math.hypot(sun.x, sun.z);
    if (len < 1e-4) return 1;
    const dx = sun.x / len;
    const dz = sun.z / len;
    const tanA = sun.y / len;
    const h0 = opts.heightAt(e.x, e.z) + 8;
    let vis = 1;
    let d = 20;
    let step = 20;
    for (let i = 0; i < 26; i++) {
      const ht = opts.heightAt(e.x + dx * d, e.z + dz * d);
      vis = Math.min(vis, Math.min(1, Math.max(0, (h0 + d * tanA - ht) / (0.010 * d + 1.2))));
      if (vis <= 0.003) break;
      d += step;
      step *= 1.14;
    }
    return vis;
  }

  function lookOf(e: StructureEntry): StructureLook | undefined {
    return looks.get(structureKey(e));
  }

  /** Every per-instance attribute that follows the look or the sun, without moving anything. */
  function paintAttributes(): void {
    for (const slot of slots.values()) {
      const n = slot.mesh.count;
      for (let j = 0; j < n; j++) {
        const owner = slot.owners[j]!;
        const e = list[owner];
        if (!e) {
          slot.light.setXYZW(j, DEFAULT_ACCENT[0], DEFAULT_ACCENT[1], DEFAULT_ACCENT[2], 0);
          slot.misc.setXYZW(j, 1, hash01(`pile${j}`), 1, 0);
          continue;
        }
        const look = lookOf(e);
        const acc = look?.accent ?? DEFAULT_ACCENT;
        slot.light.setXYZW(j, acc[0], acc[1], acc[2], MODE_CODE[look?.mode ?? 'idle']);
        const seed = hash01(`${e.cell ?? ''}${owner}`);
        slot.misc.setXYZW(j, terrainSun[owner] ?? 1, seed, look?.mode === 'active' ? 1.25 : 1, isArray(e) ? 1 : 0);
      }
      slot.light.needsUpdate = true;
      slot.misc.needsUpdate = true;
    }
    paintBillboards();
  }

  function paintBillboards(): void {
    let g = 0;
    let p = 0;
    dustPlumes = 0;
    vents = 0;
    for (let i = 0; i < list.length; i++) {
      const e = list[i]!;
      if (isArray(e)) continue;
      const look = lookOf(e);
      const mode = look?.mode ?? 'idle';
      if (mode === 'idle') continue;
      const design = DESIGNS[designKindFor(e.kind)]!;
      const spread = spreadOf(e);
      const yaw = yawOf(e);
      const c = Math.cos(yaw);
      const s = Math.sin(yaw);
      const acc = look?.accent ?? DEFAULT_ACCENT;
      const seed = hash01(e.cell ?? String(i));
      const at = (lx: number, lz: number): [number, number] => [
        e.x + lx * spread * c + lz * spread * s, e.z - lx * spread * s + lz * spread * c,
      ];
      if (g < glow.cap) {
        const [bx, bz] = at(design.beacon[0], design.beacon[2]);
        _m.makeTranslation(bx, groundUnder(bx, bz, 2.5 * PART_SCALE) + design.beacon[1] * PART_SCALE, bz);
        glow.mesh.setMatrixAt(g, _m);
        glow.light.setXYZW(g, acc[0], acc[1], acc[2], MODE_CODE[mode]);
        glow.phase.setXY(g, seed, 7);
        g++;
      }
      if (mode === 'active' && design.plume && tiers[i] !== 'far') {
        const [px, pz] = at(design.plume[0], design.plume[1]);
        const py = opts.heightAt(px, pz);
        dustPlumes++;
        for (let k = 0; k < PUFFS_PER_PLUME && p < plume.cap; k++, p++) {
          _m.makeTranslation(px, py, pz);
          plume.mesh.setMatrixAt(p, _m);
          plume.phase.setXY(p, (k + seed) / PUFFS_PER_PLUME, 6);
          plume.light.setXYZW(p, 0, 0, 0, 0);
        }
      }
      if (mode === 'active' && tiers[i] !== 'far') {
        for (const use of design.parts) {
          const def: PartDef = PARTS[use.part];
          const vs = VENTS[use.part];
          if (!vs || (def.minor && tiers[i] !== 'near')) continue;
          const [ux, uz] = at(use.x, use.z);
          const sc = (use.s ?? 1) * PART_SCALE;
          const baseY = groundUnder(ux, uz, def.r * sc);
          const py2 = yaw + (use.rot ?? 0);
          const pc = Math.cos(py2);
          const ps = Math.sin(py2);
          for (const [vx, vy, vz, kind] of vs) {
            if (p + PUFFS_PER_VENT > plume.cap) break;
            vents++;
            const wx = ux + (vx * pc + vz * ps) * sc;
            const wz = uz + (-vx * ps + vz * pc) * sc;
            const vseed = hash01(`${e.cell ?? i}:${use.part}:${vx}:${vz}`);
            for (let k = 0; k < PUFFS_PER_VENT; k++, p++) {
              _m.makeTranslation(wx, baseY + vy * sc, wz);
              plume.mesh.setMatrixAt(p, _m);
              plume.phase.setXY(p, (k + vseed) / PUFFS_PER_VENT, 4);
              plume.light.setXYZW(p, 0, 0, 0, kind);
            }
          }
        }
      }
    }
    glow.mesh.count = g;
    glow.mesh.visible = g > 0;
    glow.mesh.instanceMatrix.needsUpdate = true;
    glow.light.needsUpdate = true;
    glow.phase.needsUpdate = true;
    plume.mesh.count = p;
    plume.mesh.visible = p > 0;
    plume.mesh.instanceMatrix.needsUpdate = true;
    plume.phase.needsUpdate = true;
    plume.light.needsUpdate = true;
  }

  /**
   * A run's hauler at `now`: set into `_m` sitting on the drawn ground, nose
   * along the track, pitched and rolled to the ground under its wheels.
   */
  function haulMatrix(r: HaulRoute, now: number): { pose: ReturnType<typeof haulPose>; y: number; ground: number } {
    const pose = haulPose(r, now);
    const fx = Math.sin(pose.heading);
    const fz = Math.cos(pose.heading);
    const L = 4.5 * HAUL_SCALE;
    const W = 2.2 * HAUL_SCALE;
    const hf = opts.heightAt(pose.x + fx * L, pose.z + fz * L);
    const hb = opts.heightAt(pose.x - fx * L, pose.z - fz * L);
    const hr = opts.heightAt(pose.x + fz * W, pose.z - fx * W);
    const hl = opts.heightAt(pose.x - fz * W, pose.z + fx * W);
    const ground = opts.heightAt(pose.x, pose.z);
    _hf.set(fx * 2 * L, hf - hb, fz * 2 * L).normalize();
    _hs.set(fz * 2 * W, hr - hl, -fx * 2 * W);
    _hn.crossVectors(_hf, _hs).normalize();
    if (_hn.y < 0) _hn.negate();
    _hx.crossVectors(_hn, _hf).normalize();
    _hf.crossVectors(_hx, _hn);
    const y = Math.max(ground, (hf + hb + hr + hl) / 4) - 0.05;
    _m.makeBasis(_hx.multiplyScalar(HAUL_SCALE), _hn.multiplyScalar(HAUL_SCALE), _hf.multiplyScalar(HAUL_SCALE));
    _m.setPosition(pose.x, y, pose.z);
    return { pose, y, ground };
  }

  /** Every run's hauler, its load and its lamps, where the clock puts them this frame. */
  function paintHauls(cam: THREE.Vector3): void {
    if (haulsFrozen() && haulersDrawn > 0) return;
    const now = Date.now();
    let n = 0;
    let loads = 0;
    let lamps = 0;
    haulersMoving = 0;
    haulKeys.length = 0;
    for (const r of routes) {
      if (n >= HAUL_CAP) break;
      const { pose, y } = haulMatrix(r, now);
      if (Math.hypot(cam.x - pose.x, cam.y - y, cam.z - pose.z) > HAUL_DRAW_M) continue;
      const seed = hash01(r.key);
      haulBody.mesh.setMatrixAt(n, _m);
      const v = 0.92 + 0.12 * seed;
      haulBody.tint.setXYZ(n, PALE[0] * v, PALE[1] * v, PALE[2] * v);
      haulBody.light.setXYZW(n, 1.0, 0.55, 0.12, pose.moving ? 2 : 0);
      haulBody.misc.setXYZW(n, 1, seed, 1, 0);
      if (pose.loaded) {
        haulLoad.mesh.setMatrixAt(loads, _m);
        haulLoad.tint.setXYZ(loads, 1, 1, 1);
        haulLoad.light.setXYZW(loads, 0, 0, 0, 0);
        haulLoad.misc.setXYZW(loads, 1, seed, 1, 0);
        loads++;
      }
      if (pose.moving) haulersMoving++;
      haulMoving[n] = pose.moving ? 1 : 0;
      haulLoaded[n] = pose.loaded ? 1 : 0;
      for (const [lx, ly, lz, mode] of HAUL_LAMPS) {
        if (mode === 2 && !pose.moving) continue;
        _p.set(lx, ly, lz).applyMatrix4(_m);
        _m2.makeTranslation(_p.x, _p.y, _p.z);
        haulLamps.mesh.setMatrixAt(lamps, _m2);
        if (mode === 4) haulLamps.light.setXYZW(lamps, 1.6, 1.5, 1.3, 4);
        else haulLamps.light.setXYZW(lamps, 1.0, 0.55, 0.12, 2);
        haulLamps.phase.setXY(lamps, seed, mode === 4 ? 1.2 : 1.6);
        lamps++;
      }
      haulKeys.push(r.key);
      n++;
    }
    haulersDrawn = n;
    for (const [sl, count] of [[haulBody, n], [haulLoad, loads]] as const) {
      sl.mesh.count = count;
      sl.mesh.visible = count > 0;
      sl.mesh.instanceMatrix.needsUpdate = true;
      sl.tint.needsUpdate = true;
      sl.light.needsUpdate = true;
      sl.misc.needsUpdate = true;
    }
    haulLamps.mesh.count = lamps;
    haulLamps.mesh.visible = lamps > 0;
    haulLamps.mesh.instanceMatrix.needsUpdate = true;
    haulLamps.light.needsUpdate = true;
    haulLamps.phase.needsUpdate = true;
  }

  function setHauls(next: readonly HaulRoute[], nextPile: HaulPoint | null): void {
    routes = next;
    const moved = (pile === null) !== (nextPile === null)
      || (pile !== null && nextPile !== null && (pile.x !== nextPile.x || pile.z !== nextPile.z));
    pile = nextPile;
    if (moved) dirty = true;
  }

  function haulDebug(): ReturnType<StructureMeshes['haulDebug']> {
    const out: ReturnType<StructureMeshes['haulDebug']> = [];
    for (let j = 0; j < haulBody.mesh.count; j++) {
      haulBody.mesh.getMatrixAt(j, _m);
      const e = _m.elements;
      out.push({ key: haulKeys[j] ?? '', x: e[12]!, y: e[13]!, z: e[14]!, moving: haulMoving[j] === 1, loaded: haulLoaded[j] === 1 });
    }
    return out;
  }

  /**
   * The decks' turn about their north-south tubes: square to the sun's
   * east-west angle, up to TRACK_MAX, positive facing east (morning), and
   * level (stowed) with the sun down. A turn of r takes a deck's normal from
   * +y to (sin r, cos r, 0) on the plot.
   */
  function trackRoll(): number {
    const sun = u['uSunDir']!.value as THREE.Vector3;
    if (sun.y < 0.03) return 0;
    return THREE.MathUtils.clamp(Math.atan2(sun.x, sun.y), -TRACK_MAX, TRACK_MAX);
  }

  /** Every drawn deck turned to `track` where it stands, in place: no rebuild, nothing allocated. */
  function turnDecks(): void {
    _q2.setFromAxisAngle(_up, ROW_YAW).multiply(_q.setFromAxisAngle(_xax, track));
    for (const [key, slot] of slots) {
      if (!key.startsWith('panelDeck:') || slot.mesh.count === 0) continue;
      for (let j = 0; j < slot.mesh.count; j++) {
        slot.mesh.getMatrixAt(j, _m);
        _p.setFromMatrixPosition(_m);
        slot.mesh.setMatrixAt(j, _m.compose(_p, _q2, _s.set(1, 1, 1)));
      }
      slot.mesh.instanceMatrix.needsUpdate = true;
    }
  }

  /** The site pile the haulers load at, drawn only while a run starts or ends there. Owner -1. */
  function placePile(out: Placed[]): 'hi' | 'lo' | null {
    if (!pile) return null;
    const d = Math.hypot(lastCam.x - pile.x, lastCam.z - pile.z);
    if (d > MID_M) return null;
    for (const use of PILE_PARTS) {
      const x = pile.x + use.x;
      const z = pile.z + use.z;
      const sc = use.s * PART_SCALE;
      const m = new THREE.Matrix4().compose(
        _p.set(x, groundUnder(x, z, PARTS[use.part].r * sc), z), _q.setFromAxisAngle(_up, 0.6), _s.set(sc, sc, sc));
      out.push({ part: use.part, m, owner: -1 });
    }
    return d < NEAR_M ? 'hi' : 'lo';
  }

  /** Lays every instance down again: a new list, a tier change, or new relief. */
  function rebuild(): void {
    rebuilds++;
    const placed: Placed[] = [];
    for (let i = 0; i < list.length; i++) {
      const e = list[i]!;
      const tier = tiers[i] ?? 'far';
      if (tier === 'far') continue;
      if (isArray(e)) placeArray(i, e, placed);
      else placeCompound(i, e, placed, tier === 'near');
    }
    const pileLod = placePile(placed);
    const byKey = new Map<string, Placed[]>();
    for (const pl of placed) {
      const lod = pl.owner < 0 ? pileLod! : tiers[pl.owner] === 'near' ? 'hi' : 'lo';
      const key = `${pl.part}:${lod}`;
      let arr = byKey.get(key);
      if (!arr) { arr = []; byKey.set(key, arr); }
      arr.push(pl);
    }
    for (const [key, slot] of slots) {
      if (!byKey.has(key)) { slot.mesh.count = 0; slot.mesh.visible = false; }
    }
    for (const [key, arr] of byKey) {
      const [part, lod] = key.split(':') as [PartId, 'hi' | 'lo'];
      const slot = slotFor(part, lod, arr.length);
      const tint = PARTS[part].tint;
      for (let j = 0; j < arr.length; j++) {
        const pl = arr[j]!;
        slot.mesh.setMatrixAt(j, pl.m);
        slot.owners[j] = pl.owner;
        const v = 0.94 + 0.12 * hash01(`${list[pl.owner]?.cell ?? ''}:${part}:${j}`);
        slot.tint.setXYZ(j, tint[0] * v, tint[1] * v, tint[2] * v);
      }
      slot.mesh.count = arr.length;
      slot.mesh.visible = arr.length > 0;
      slot.mesh.instanceMatrix.needsUpdate = true;
      slot.tint.needsUpdate = true;
      slot.mesh.computeBoundingSphere();
    }
    paintAttributes();
  }

  function set(next: readonly StructureEntry[]): void {
    list = next.map((e) => ({ ...e }));
    tiers = [];
    terrainSun = list.map(marchSun);
    track = trackRoll();
    dirty = true;
  }

  function setLooks(next: ReadonlyMap<string, StructureLook>): void {
    looks = next;
    paintAttributes();
  }

  function update(camLocal: THREE.Vector3, camera?: THREE.Camera, paneHeightPx?: number): void {
    const persp = camera as THREE.PerspectiveCamera | undefined;
    if (persp?.isPerspectiveCamera && paneHeightPx) {
      minPxK.value = (BEACON_MIN_PX * 2 * Math.tan(THREE.MathUtils.degToRad(persp.fov) / 2)) / paneHeightPx;
    }
    lastCam.copy(camLocal);
    if (list.length === 0) {
      if (dirty) { dirty = false; rebuild(); }
      if (routes.length > 0 || haulersDrawn > 0) paintHauls(camLocal);
      return;
    }
    const exag = u['uExag']?.value as number | undefined;
    if (exag !== lastExag) { lastExag = exag ?? NaN; dirty = true; }
    const sun = u['uSunDir']!.value as THREE.Vector3;
    const sunMoved = oldSunGuard() ? !sun.equals(lastPaintedSun) : sun.angleTo(lastPaintedSun) > SUN_REPAINT_EPS_RAD;
    if (!Number.isFinite(lastPaintedSun.x) || sunMoved) {
      lastPaintedSun.copy(sun);
      terrainSun = list.map(marchSun);
      sunRepaints++;
      const roll = trackRoll();
      if (Math.abs(roll - track) > TRACK_STEP) { track = roll; turnDecks(); }
      if (!dirty) paintAttributes();
    }
    for (let i = 0; i < list.length; i++) {
      const t = tierOf(i, list[i]!, camLocal);
      if (t !== tiers[i]) { tiers[i] = t; dirty = true; }
    }
    if (pile) {
      const d = Math.hypot(camLocal.x - pile.x, camLocal.z - pile.z);
      const band = d < NEAR_M ? 0 : d < MID_M ? 1 : 2;
      if (band !== pileBand) { pileBand = band; dirty = true; }
    }
    if (dirty) {
      dirty = false;
      if (exag !== undefined) terrainSun = list.map(marchSun);
      rebuild();
    }
    if (routes.length > 0 || haulersDrawn > 0) paintHauls(camLocal);
  }

  function setCastShadow(on: boolean): void {
    castOn = on;
    for (const [key, slot] of slots) slot.mesh.castShadow = on && key.endsWith(':hi');
    haulBody.mesh.castShadow = on;
    haulLoad.mesh.castShadow = on;
  }

  const raycaster = new THREE.Raycaster();
  const hits: THREE.Intersection[] = [];

  function pick(ray: THREE.Ray): StructureEntry | null {
    raycaster.ray.copy(ray);
    raycaster.far = Infinity;
    let best: { d: number; owner: number } | null = null;
    for (const slot of slots.values()) {
      if (!slot.mesh.visible || slot.mesh.count === 0) continue;
      hits.length = 0;
      slot.mesh.raycast(raycaster, hits);
      for (const h of hits) {
        if (h.instanceId === undefined) continue;
        if (!best || h.distance < best.d) best = { d: h.distance, owner: slot.owners[h.instanceId]! };
      }
    }
    return best ? list[best.owner] ?? null : null;
  }

  function stats(): StructureMeshStats {
    const t: Record<StructureTier, number> = { near: 0, mid: 0, far: 0 };
    for (const x of tiers) t[x]++;
    let instances = 0;
    let meshes = 0;
    let triangles = 0;
    let floatMaxM = -Infinity;
    let sinkMaxM = 0;
    let deckSinkM = 0;
    for (const [key, slot] of slots) {
      if (!slot.mesh.visible || slot.mesh.count === 0) continue;
      instances += slot.mesh.count;
      meshes++;
      triangles += (slot.geo.getAttribute('position').count / 3) * slot.mesh.count;
      // A deck's origin is its pivot, a tube's height up by design; what must
      // not go under is its box, turned as it is drawn.
      if (key.startsWith('panelDeck:')) {
        if (!slot.geo.boundingBox) slot.geo.computeBoundingBox();
        const b = slot.geo.boundingBox!;
        for (let j = 0; j < slot.mesh.count; j++) {
          slot.mesh.getMatrixAt(j, _m);
          for (let c = 0; c < 8; c++) {
            _p.set(c & 1 ? b.max.x : b.min.x, c & 2 ? b.max.y : b.min.y, c & 4 ? b.max.z : b.min.z).applyMatrix4(_m);
            deckSinkM = Math.max(deckSinkM, opts.heightAt(_p.x, _p.z) - _p.y);
          }
        }
        continue;
      }
      for (let j = 0; j < slot.mesh.count; j++) {
        slot.mesh.getMatrixAt(j, _m);
        const e = _m.elements;
        const above = e[13]! - opts.heightAt(e[12]!, e[14]!);
        floatMaxM = Math.max(floatMaxM, above);
        sinkMaxM = Math.max(sinkMaxM, -above);
      }
    }
    return {
      buildings: list.length, tiers: t, instances, meshes, triangles,
      beacons: glow.mesh.count, plumes: dustPlumes, vents, haulers: haulersDrawn, haulersMoving,
      trackDeg: THREE.MathUtils.radToDeg(track), rebuilds, sunRepaints,
      floatMaxM: instances > 0 ? floatMaxM : 0, sinkMaxM, deckSinkM,
    };
  }

  function dispose(): void {
    for (const slot of slots.values()) {
      group.remove(slot.mesh);
      slot.mesh.dispose();
      slot.geo.dispose();
    }
    slots.clear();
    for (const sl of [haulBody, haulLoad]) {
      group.remove(sl.mesh);
      sl.mesh.dispose();
      sl.geo.dispose();
    }
    for (const b of [glow, plume, haulLamps]) {
      group.remove(b.mesh);
      b.mesh.dispose();
      b.geo.dispose();
    }
    material.dispose();
    glowMat.dispose();
    plumeMat.dispose();
  }

  return {
    group, set, setLooks, setHauls, haulDebug, update, setCastShadow, pick,
    entries: () => list, stats, dispose,
  };
}

/**
 * The parts', the beacons' and the dust's programs, built off degenerate
 * triangles for the descent's shader warm (shaderWarm.ts), so the first
 * building to arrive links nothing on the frame it appears. The part
 * prototype CASTS, because its instanced depth variant is a program of its
 * own that `renderer.compile` never reaches. `csmGlsl` must be what the plot
 * hands `createStructureMeshes`: the cascade chunk is in the source.
 */
export function warmStructurePrograms(csmGlsl: string | null): { readonly group: THREE.Group; dispose(): void } {
  // A uniform's VALUE is no part of a program's cache key; these are here so
  // three finds every key the materials read.
  const u: Record<string, THREE.IUniform> = {
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uSunFlux: { value: 1 },
    uSunCol: { value: new THREE.Vector3(1, 1, 1) },
    uSkyCol: { value: new THREE.Vector3(1, 1, 1) },
    uBounceCol: { value: new THREE.Vector3(1, 1, 1) },
    uCamLocal: { value: new THREE.Vector3() },
    uTime: { value: 0 },
  };
  const minPxK: THREE.IUniform = { value: 0 };
  const mats = [partMaterial(u, csmGlsl), glowMaterial(u, minPxK), plumeMaterial(u, minPxK)];
  const names = ['structure-warm-part', 'structure-warm-beacon', 'structure-warm-plume'];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(9), 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array([0, 1, 0, 0, 1, 0, 0, 1, 0]), 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(6), 2));
  const group = new THREE.Group();
  group.name = 'structure-program-warm';
  for (let i = 0; i < mats.length; i++) {
    mats[i]!.name = names[i]!;
    const mesh = new THREE.InstancedMesh(geometry, mats[i]!, 1);
    mesh.count = 0;
    mesh.castShadow = i === 0;
    mesh.frustumCulled = false;
    group.add(mesh);
  }
  return {
    group,
    dispose(): void {
      for (const m of mats) m.dispose();
      geometry.dispose();
    },
  };
}
